import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { runGeneration } from "@/lib/generate";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AGE_GROUPS, LANGUAGES, THEMES, DOMAINS, ACTIVITY_TYPES, DURATIONS, label,
} from "@/lib/activityData";

export default function ActivityGenerator() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    age_group: "", language: "bilingual", theme: "", domains: [], activity_type: "", duration: "",
  });
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDomain = (d) =>
    setForm((f) => ({ ...f, domains: f.domains.includes(d) ? f.domains.filter((x) => x !== d) : [...f.domains, d] }));

  const canSubmit = form.age_group && form.theme && form.activity_type && form.duration && !loading;

  const submit = async () => {
    if (!canSubmit) {
      toast.error(lang === "es" ? "Completa los campos requeridos." : "Please fill the required fields.");
      return;
    }
    setLoading(true);
    setElapsed(0);
    try {
      const j = await runGeneration("activity", form, { onTick: setElapsed });
      toast.success(t("activity.saved"));
      navigate(`/activities/${j.result_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <GeneratingOverlay open={loading} elapsed={elapsed} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav.dashboard")}
        </button>

        <div className="space-y-2">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("generator.title")}</h1>
          <p className="text-muted-foreground text-lg">{t("generator.subtitle")}</p>
        </div>

        <div className="space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          {/* Age group */}
          <div className="space-y-2">
            <Label>{t("generator.ageGroup")} <span className="text-secondary">*</span></Label>
            <Select value={form.age_group} onValueChange={(v) => set("age_group", v)}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="gen-age"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label>{t("generator.language")} <span className="text-secondary">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => (
                <button key={l.value} onClick={() => set("language", l.value)} data-testid={`gen-lang-${l.value}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${form.language === l.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {label(l, lang)}
                </button>
              ))}
            </div>
          </div>

          {/* Theme */}
          <div className="space-y-2">
            <Label htmlFor="theme">{t("generator.theme")} <span className="text-secondary">*</span></Label>
            <Input id="theme" value={form.theme} onChange={(e) => set("theme", e.target.value)} placeholder={t("generator.themePlaceholder")} className="h-12 rounded-xl" data-testid="gen-theme" />
            <div className="flex flex-wrap gap-2 pt-1">
              {THEMES.map((th, i) => (
                <button key={i} onClick={() => set("theme", label(th, lang))} data-testid={`theme-chip-${i}`}
                  className="rounded-full bg-muted hover:bg-muted/70 px-3 py-1 text-sm text-muted-foreground">
                  {label(th, lang)}
                </button>
              ))}
            </div>
          </div>

          {/* Domains */}
          <div className="space-y-2">
            <Label>{t("generator.domain")} <span className="text-muted-foreground text-sm font-normal">({t("generator.domainHint")})</span></Label>
            <div className="flex flex-wrap gap-2">
              {DOMAINS.map((d) => {
                const on = form.domains.includes(d.value);
                return (
                  <button key={d.value} onClick={() => toggleDomain(d.value)} data-testid={`gen-domain-${d.value.replace(/\W+/g, "-")}`}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${on ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    {label(d, lang)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Activity type + duration */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("generator.activityType")} <span className="text-secondary">*</span></Label>
              <Select value={form.activity_type} onValueChange={(v) => set("activity_type", v)}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="gen-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("generator.duration")} <span className="text-secondary">*</span></Label>
              <Select value={form.duration} onValueChange={(v) => set("duration", v)}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="gen-duration"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{label(d, lang)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={submit} disabled={!canSubmit} className="w-full h-13 py-3 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="gen-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("generator.generating")}</> : <><Sparkles className="h-5 w-5" /> {t("generator.generateBtn")}</>}
          </Button>
        </div>

        {loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-muted-foreground">
            <p>{t("generator.generating")}</p>
          </motion.div>
        )}
      </main>
    </div>
  );
}
